import {
  BAR_PATHS_REST,
  BAR_PATHS_STAGGERED,
  BAR_START_OFFSETS,
  SWEEP_DURATION_MS,
} from "./logoMarkPaths";

/**
 * The mark morphs by interpolating the real path geometry of its two states,
 * number by number, so the bars never clip or snap.
 */

type Segment = {
  /** Path command letter, e.g. "M", "C", "Z". */
  readonly command: string;
  /** Matched [rest, staggered] number pairs for this command. */
  readonly pairs: readonly (readonly [number, number])[];
};

export type Bar = {
  readonly segments: readonly Segment[];
  /** Fraction of the sweep at which this bar starts moving. */
  readonly start: number;
  /** CSS class carrying the bar's fill. */
  readonly className: string;
};

const SEGMENT_PATTERN = /([MLCSQTAHVZmlcsqtahvz])([^MLCSQTAHVZmlcsqtahvz]*)/g;
const NUMBER_PATTERN = /-?\d*\.?\d+(?:e-?\d+)?/g;

const parseSegments = (path: string): readonly { command: string; numbers: number[] }[] => {
  const segments: { command: string; numbers: number[] }[] = [];
  for (const match of path.matchAll(SEGMENT_PATTERN)) {
    const [, command = "", body = ""] = match;
    segments.push({ command, numbers: (body.match(NUMBER_PATTERN) ?? []).map(Number) });
  }
  return segments;
};

/**
 * Pairs the two states up front. Any structural mismatch throws at module load
 * rather than producing a silently broken path at hover time.
 */
const pairStates = (restPath: string, staggeredPath: string): readonly Segment[] => {
  const rest = parseSegments(restPath);
  const staggered = parseSegments(staggeredPath);

  if (rest.length !== staggered.length) {
    throw new Error(
      `Logo mark states differ in structure: ${String(rest.length)} vs ${String(staggered.length)} segments.`,
    );
  }

  return rest.map((segment, index) => {
    const counterpart = staggered[index];
    if (counterpart?.command !== segment.command) {
      throw new Error(`Logo mark segment ${String(index)} does not match between states.`);
    }

    const pairs = segment.numbers.map((value, numberIndex): readonly [number, number] => {
      const target = counterpart.numbers[numberIndex];
      if (target === undefined) {
        throw new Error(`Logo mark segment ${String(index)} has mismatched number counts.`);
      }
      return [value, target];
    });

    return { command: segment.command, pairs };
  });
};

/**
 * The bars carry a graded ramp rather than four separate identities, so the
 * classes are positional: bar 1 is the accent, bars 2-4 recede.
 */
const BAR_CLASS_NAMES: readonly string[] = [
  "logo__bar logo__bar--1",
  "logo__bar logo__bar--2",
  "logo__bar logo__bar--3",
  "logo__bar logo__bar--4",
];

export const BARS: readonly Bar[] = BAR_PATHS_REST.map((restPath, index) => {
  const staggeredPath = BAR_PATHS_STAGGERED[index];
  const start = BAR_START_OFFSETS[index];
  const className = BAR_CLASS_NAMES[index];

  if (staggeredPath === undefined || start === undefined || className === undefined) {
    throw new Error(`Logo mark bar ${String(index)} is missing its counterpart, offset, or class.`);
  }

  return { segments: pairStates(restPath, staggeredPath), start, className };
});

/** Longest start offset; every bar morphs over the remaining window. */
const LAST_START = BAR_START_OFFSETS.reduce((longest, start) => Math.max(longest, start), 0);
const BAR_WINDOW = 1 - LAST_START;

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value);

/** Path `d` for one bar at a given point in the overall sweep. */
export const barPathAt = (bar: Bar, sweep: number): string => {
  const eased = easeInOutCubic(clamp01((sweep - bar.start) / BAR_WINDOW));

  let path = "";
  for (const segment of bar.segments) {
    path += segment.command;
    path += segment.pairs.map(([from, to]) => (from + (to - from) * eased).toFixed(3)).join(" ");
  }
  return path;
};

export { SWEEP_DURATION_MS };
